import { useEffect, useState, useRef } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, FileText, CheckCircle, XCircle, Clock } from "lucide-react";

const docTypeLabels = {
  id_document: "ID Document",
  proof_of_registration: "Proof of Registration",
  sponsorship_letter: "Sponsorship Letter",
  proof_of_address: "Proof of Address",
  other: "Other",
};

const statusIcons = {
  uploaded: <Clock className="h-4 w-4 text-amber-600" />,
  approved: <CheckCircle className="h-4 w-4 text-emerald-600" />,
  rejected: <XCircle className="h-4 w-4 text-red-600" />,
};

const statusColors = {
  uploaded: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
};

export default function TenantDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("");
  const fileInputRef = useRef(null);

  const fetchDocs = () => {
    api.get("/documents").then((r) => setDocuments(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { fetchDocs(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!docType) { toast.error("Please select a document type first"); return; }
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) { toast.error("File too large (max 10MB)"); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/documents/upload?doc_type=${docType}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded successfully");
      setDocType("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchDocs();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const approved = documents.filter((d) => d.status === "approved").length;
  const pending = documents.filter((d) => d.status === "uploaded").length;
  const rejected = documents.filter((d) => d.status === "rejected").length;

  return (
    <DashboardLayout title="My Documents">
      <div className="space-y-6" data-testid="tenant-documents-page">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-border/60"><CardContent className="pt-6 text-center">
            <div className="font-heading text-2xl font-bold text-emerald-700">{approved}</div>
            <p className="text-xs text-muted-foreground">Approved</p>
          </CardContent></Card>
          <Card className="border-border/60"><CardContent className="pt-6 text-center">
            <div className="font-heading text-2xl font-bold text-amber-700">{pending}</div>
            <p className="text-xs text-muted-foreground">Pending Review</p>
          </CardContent></Card>
          <Card className="border-border/60"><CardContent className="pt-6 text-center">
            <div className="font-heading text-2xl font-bold text-red-700">{rejected}</div>
            <p className="text-xs text-muted-foreground">Rejected</p>
          </CardContent></Card>
        </div>

        {/* Upload */}
        <Card className="border-border/60" data-testid="upload-section">
          <CardHeader><CardTitle className="font-heading text-lg">Upload Document</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div className="w-full sm:w-48">
                <Label>Document Type</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="doc-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(docTypeLabels).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label>File (PDF, JPG, PNG - max 10MB)</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleUpload}
                  disabled={uploading || !docType}
                  className="mt-1.5 block w-full text-sm text-muted-foreground file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#0A2540] file:text-white hover:file:bg-[#0A2540]/90 file:cursor-pointer disabled:opacity-50"
                  data-testid="file-upload-input"
                />
              </div>
              {uploading && <Loader2 className="h-5 w-5 animate-spin text-primary mt-2" />}
            </div>
          </CardContent>
        </Card>

        {/* Documents Table */}
        <Card className="border-border/60">
          <CardHeader><CardTitle className="font-heading text-lg">Uploaded Documents</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                No documents uploaded yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((d) => (
                    <TableRow key={d._id} data-testid={`doc-row-${d._id}`}>
                      <TableCell className="font-medium">{docTypeLabels[d.doc_type] || d.doc_type}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{d.original_filename}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {statusIcons[d.status]}
                          <Badge className={statusColors[d.status]}>{d.status}</Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{d.admin_notes || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
